import { useUserPreferences } from '@/providers/UserPreferencesProvider';

/**
 * Hook to get display density-aware CSS classes
 * 
 * Usage:
 * const density = useDisplayDensity();
 * <div className={density.row}>...</div>
 */
export function useDisplayDensity() {
  const { preferences } = useUserPreferences();
  const isCompact = preferences.displayDensity === 'compact';

  return {
    // Row heights for tables and data grids
    row: isCompact ? 'h-9' : 'h-12', // 36px vs 48px
    
    // Padding variants
    padding: {
      sm: isCompact ? 'p-1' : 'p-2',
      md: isCompact ? 'p-2' : 'p-3',
      lg: isCompact ? 'p-3' : 'p-4',
      xl: isCompact ? 'p-4' : 'p-6',
    },
    
    // Padding X (horizontal)
    px: {
      sm: isCompact ? 'px-1' : 'px-2',
      md: isCompact ? 'px-2' : 'px-3',
      lg: isCompact ? 'px-3' : 'px-4',
      xl: isCompact ? 'px-4' : 'px-6',
    },
    
    // Padding Y (vertical)
    py: {
      sm: isCompact ? 'py-1' : 'py-2',
      md: isCompact ? 'py-2' : 'py-3',
      lg: isCompact ? 'py-3' : 'py-4',
      xl: isCompact ? 'py-4' : 'py-6',
    },
    
    // Gap between elements
    gap: {
      sm: isCompact ? 'gap-1' : 'gap-2',
      md: isCompact ? 'gap-2' : 'gap-4',
      lg: isCompact ? 'gap-3' : 'gap-6',
      xl: isCompact ? 'gap-4' : 'gap-8',
    },
    
    // Space between elements
    space: {
      sm: isCompact ? 'space-y-1' : 'space-y-2',
      md: isCompact ? 'space-y-2' : 'space-y-4',
      lg: isCompact ? 'space-y-3' : 'space-y-6',
      xl: isCompact ? 'space-y-4' : 'space-y-8',
    },
    
    // Font sizes
    text: {
      xs: isCompact ? 'text-[11px]' : 'text-xs',
      sm: isCompact ? 'text-xs' : 'text-sm',
      base: isCompact ? 'text-[13px]' : 'text-sm',
      lg: isCompact ? 'text-sm' : 'text-base',
    },
    
    // Button size variant
    buttonSize: isCompact ? 'sm' : 'default' as 'sm' | 'default',
    
    // Icon sizes
    icon: {
      sm: isCompact ? 'h-3 w-3' : 'h-4 w-4',
      md: isCompact ? 'h-4 w-4' : 'h-5 w-5',
      lg: isCompact ? 'h-5 w-5' : 'h-6 w-6',
    },
    
    // Table cell padding
    cell: isCompact ? 'px-2 py-1.5' : 'px-3 py-3',
    
    // Table header padding
    header: isCompact ? 'px-2 py-2' : 'px-3 py-3',
    
    // Card padding
    card: isCompact ? 'p-3' : 'p-4',
    
    // Check if compact mode is active
    isCompact,
  };
}
